import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Badge } from 'react-bootstrap';
import {
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { AiOutlineVerticalAlignBottom } from 'react-icons/ai';
import { toast } from 'react-toastify';
import { AddCommasToAmount } from '../../helper/Utils';
import { extractDocumentServePath, openSecureDocumentOrToast } from '../../helper/secureDocuments';

const attachmentValue = (challan) => {
    const stored = String(challan?.challan_url || challan?.document_ref || '').trim();
    if (!stored || stored === '0' || stored.toLowerCase() === 'null') return '';
    return stored;
};

const formatDate = (value) => {
    if (!value) return '-';
    const parsed = moment(value, ['YYYY-MM-DD', 'DD-MM-YYYY', 'YYYY-MM-DD HH:mm:ss'], true);
    return parsed.isValid() ? parsed.format('DD-MM-YYYY') : value;
};

const formatMoney = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return AddCommasToAmount(value) || '-';
};

const sectionLabel = (row) => {
    const label = row?.tds_section || row?.tds_section_name || row?.section || '';
    if (label) return String(label).replace(/^section\s+/i, 'Section ');
    const id = row?.tds_section_id ?? row?.tds_it_section;
    if (id !== null && id !== undefined && String(id).trim() !== '') return `Section ${id}`;
    return '';
};

const challanDocumentRef = (challan) => {
    const servePath = extractDocumentServePath(attachmentValue(challan));
    if (servePath) return servePath;
    const id = challan?.challan_id;
    if (id == null || String(id).trim() === '') return '';
    return `documents/serve/tds_challan/${id}`;
};

const hasChallanAttachment = (challan) => Boolean(challanDocumentRef(challan) || attachmentValue(challan));

const toChallan = (row, invoices, index) => ({
    key: String(row?.challan_id || row?.tds_challan_id || row?.challan_no || index),
    challan_id: row?.challan_id || row?.tds_challan_id || '',
    challan_no: row?.challan_no || '',
    challan_date: row?.challan_date || '',
    cheque_no: row?.cheque_no || '',
    cheque_date: row?.cheque_date || '',
    tds_challan_value: row?.tds_challan_value,
    challan_url: row?.challan_url || row?.document_ref || '',
    invoices,
});

const challanGroupKey = (row, index) => {
    const challanNo = String(row?.challan_no || '').trim();
    if (challanNo) return `no:${challanNo}|${row?.challan_date || ''}`;
    return `row:${row?.invoice_id || row?.id || index}`;
};

const challanHeaderValue = (group) => {
    const distinct = [...new Set(
        group.invoices
            .map((invoice) => Number(invoice?.tds_challan_value))
            .filter((amount) => !Number.isNaN(amount) && amount > 0)
            .map((amount) => amount.toFixed(2))
    )];
    if (distinct.length === 1) return distinct[0];
    if (distinct.length > 1) {
        const total = distinct.reduce((sum, amount) => sum + Number(amount), 0);
        return total.toFixed(2);
    }
    const invoiceTotal = group.invoices.reduce((sum, invoice) => sum + (Number(invoice?.tds_it_amount) || 0), 0);
    return invoiceTotal ? invoiceTotal.toFixed(2) : '';
};

const groupFlatInvoices = (rows) => {
    const groups = new Map();
    rows.forEach((row, index) => {
        const challanId = row?.challan_id || row?.tds_challan_id || '';
        const key = challanGroupKey(row, index);
        if (!groups.has(key)) {
            groups.set(key, toChallan(row, [], index));
            groups.get(key).key = key;
        }
        const group = groups.get(key);
        if (!group.cheque_no && row?.cheque_no) group.cheque_no = row.cheque_no;
        if (!group.cheque_date && row?.cheque_date) group.cheque_date = row.cheque_date;
        if (!group.challan_url && (row?.challan_url || row?.document_ref)) {
            group.challan_url = row.challan_url || row.document_ref;
        }
        if (!group.challan_id && challanId) group.challan_id = challanId;
        const invoiceId = row?.invoice_id || row?.id;
        const alreadyAdded = invoiceId != null && group.invoices.some(
            (item) => String(item?.invoice_id || item?.id) === String(invoiceId)
        );
        if (!alreadyAdded) group.invoices.push(row);
    });
    return Array.from(groups.values()).map((group) => {
        group.tds_challan_value = challanHeaderValue(group);
        return group;
    });
};

const normalizeChallans = (list) => {
    if (!Array.isArray(list) || !list.length) return [];
    const nested = list.some((row) => Array.isArray(row?.invoices));
    if (!nested) return groupFlatInvoices(list);
    return list.map((row, index) => toChallan(
        {
            ...row,
            challan_id: row?.challan_id || row?.tds_challan_id || row?.id,
        },
        row.invoices || [],
        index
    ));
};

const headCellSx = {
    fontWeight: 600,
    fontSize: 13,
    color: '#28326f',
    backgroundColor: '#f4f6fb',
    whiteSpace: 'nowrap',
};

const expandColSx = {
    ...headCellSx,
    width: 40,
    minWidth: 40,
    maxWidth: 40,
    px: 0.5,
    textAlign: 'center',
};

const actionColSx = {
    ...headCellSx,
    width: 70,
    minWidth: 70,
    maxWidth: 70,
    px: 0.5,
    textAlign: 'center',
};

const SectionBadges = ({ invoices }) => {
    const labels = [...new Set(invoices.map(sectionLabel).filter(Boolean))];
    if (!labels.length) return <span className="text-muted">-</span>;
    return (
        <div className="d-flex flex-wrap gap-1">
            {labels.map((label) => (
                <Badge key={label} bg="info" style={{ fontSize: 12, fontWeight: 600 }}>
                    {label}
                </Badge>
            ))}
        </div>
    );
};

const InvoiceTable = ({ invoices }) => (
    <Table size="small" sx={{ backgroundColor: '#fff' }}>
        <TableHead>
            <TableRow>
                {['Beneficiary', 'Invoice', 'Voucher', 'Taxable Amount', 'Invoice Amount', 'TDS IT', 'Section'].map((label) => (
                    <TableCell key={label} sx={{ ...headCellSx, backgroundColor: '#eef2f8' }}>{label}</TableCell>
                ))}
            </TableRow>
        </TableHead>
        <TableBody>
            {invoices.map((invoice, index) => (
                <TableRow key={`${invoice?.invoice_id || invoice?.id || index}`}>
                    <TableCell>
                        <div style={{ fontWeight: 600 }}>{invoice?.company_name || invoice?.beneficiary_name || '-'}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                            PAN: {invoice?.pan_no || '-'}
                        </div>
                    </TableCell>
                    <TableCell>
                        <div style={{ fontWeight: 600 }}>{invoice?.invoice_no || '-'}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>{formatDate(invoice?.invoice_date)}</div>
                    </TableCell>
                    <TableCell>
                        <div style={{ fontWeight: 600 }}>{invoice?.voucher_no || '-'}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>{formatDate(invoice?.voucher_date)}</div>
                    </TableCell>
                    <TableCell>{formatMoney(invoice?.taxable_amount)}</TableCell>
                    <TableCell>{formatMoney(invoice?.payable_amount || invoice?.invoice_value)}</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>{formatMoney(invoice?.tds_it_amount)}</TableCell>
                    <TableCell>
                        {sectionLabel(invoice) ? (
                            <Badge bg="info" style={{ fontSize: 12, fontWeight: 600 }}>
                                {sectionLabel(invoice)}
                            </Badge>
                        ) : '-'}
                    </TableCell>
                </TableRow>
            ))}
        </TableBody>
    </Table>
);

const TDSReportList = ({ data, amountTick = 0 }) => {
    const challans = useMemo(() => normalizeChallans(data), [data]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [openKey, setOpenKey] = useState('');

    useEffect(() => {
        setPage(0);
        setOpenKey('');
    }, [data]);

    const pageRows = challans.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const openChallan = (challan) => {
        const ref = challanDocumentRef(challan);
        if (!ref) {
            toast.error('Document unavailable', {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }
        openSecureDocumentOrToast(ref, {
            fallbackFilename: `tds_challan_${challan.challan_no || challan.challan_id || 'document'}`,
        });
    };

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden' }} data-amount-tick={amountTick}>
            <TableContainer sx={{ maxHeight: '72vh' }}>
                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                    <colgroup>
                        <col style={{ width: 40 }} />
                        <col style={{ width: 70 }} />
                        <col />
                        <col />
                        <col />
                        <col style={{ width: 90 }} />
                        <col />
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={expandColSx} />
                            <TableCell sx={actionColSx}>Actions</TableCell>
                            <TableCell sx={headCellSx}>Challan No / Date</TableCell>
                            <TableCell sx={headCellSx}>Cheque No / Date</TableCell>
                            <TableCell sx={headCellSx}>Challan Value</TableCell>
                            <TableCell sx={headCellSx}>Invoices</TableCell>
                            <TableCell sx={headCellSx}>Section</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!pageRows.length && (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#6b7280' }}>
                                    No challans found
                                </TableCell>
                            </TableRow>
                        )}
                        {pageRows.map((challan) => {
                            const expanded = openKey === challan.key;
                            const canDownload = hasChallanAttachment(challan);
                            return (
                                <React.Fragment key={challan.key}>
                                    <TableRow hover>
                                        <TableCell sx={expandColSx}>
                                            <IconButton
                                                size="small"
                                                aria-label={expanded ? 'Collapse invoices' : 'Expand invoices'}
                                                onClick={() => setOpenKey(expanded ? '' : challan.key)}
                                            >
                                                {expanded
                                                    ? <KeyboardArrowDownIcon fontSize="small" />
                                                    : <KeyboardArrowRightIcon fontSize="small" />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell sx={actionColSx}>
                                            {canDownload && (
                                                <Tooltip title="Download challan" arrow>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => openChallan(challan)}
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            border: '1px solid #1976d2',
                                                            color: '#1976d2',
                                                            borderRadius: '6px',
                                                        }}
                                                    >
                                                        <AiOutlineVerticalAlignBottom size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div style={{ fontWeight: 600 }}>{challan.challan_no || '-'}</div>
                                            <div className="text-muted" style={{ fontSize: 12 }}>{formatDate(challan.challan_date)}</div>
                                        </TableCell>
                                        <TableCell>
                                            <div style={{ fontWeight: 600 }}>{challan.cheque_no || '-'}</div>
                                            <div className="text-muted" style={{ fontSize: 12 }}>{formatDate(challan.cheque_date)}</div>
                                        </TableCell>
                                        <TableCell style={{ fontWeight: 600 }}>{formatMoney(challan.tds_challan_value)}</TableCell>
                                        <TableCell>{challan.invoices.length}</TableCell>
                                        <TableCell><SectionBadges invoices={challan.invoices} /></TableCell>
                                    </TableRow>
                                    {expanded && (
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ backgroundColor: '#f8fafc', p: 1.5 }}>
                                                <InvoiceTable invoices={challan.invoices} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={challans.length}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                onPageChange={(_, nextPage) => {
                    setPage(nextPage);
                    setOpenKey('');
                }}
                onRowsPerPageChange={(event) => {
                    setRowsPerPage(Number(event.target.value));
                    setPage(0);
                    setOpenKey('');
                }}
            />
        </Paper>
    );
};

export default TDSReportList;
